import { useNavigation } from "@react-navigation/native";
import firebase from "firebase/app";
import "firebase/auth";
import React, { FC, useState } from "react";
import { ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import { useSelector } from "react-redux";

import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { validatePassword } from "src/redux/user/actions";

const P01_04_ResetPassword: FC = () => {
  const kNav = useNavigation();
  const dispatch = useAppDispatch();
  const rIsMobile = useSelector(
    (state) => state.NavigationReducer.deviceSize === "xs" || state.NavigationReducer.deviceSize === "sm",
  );
  const [sNewPassword, setNewPassword] = useState<string>("");
  const [sConfirmPassword, setConfirmPassword] = useState<string>("");
  const searchParams = new URLSearchParams(window.location.search);

  // FIXME: This should be in a thunk action
  const sendNewPassword = (): void => {
    if (sNewPassword !== sConfirmPassword) {
      dispatch({
        type: "RIBBON",
        message: new Error("Passwords do not match"),
      });
      return undefined;
    }
    if (!validatePassword(sNewPassword)) {
      dispatch({
        type: "RIBBON",
        message: new Error(
          "Password must be at least 8 characters, and contain at least one capital letter, number, and a special character.",
        ),
      });
      return undefined;
    }
    firebase
      .auth()
      .verifyPasswordResetCode(searchParams.get("oobCode") ?? "")
      .then(() => {
        return firebase.auth().confirmPasswordReset(searchParams.get("oobCode") ?? "", sNewPassword);
      })
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Password successfully changed.",
        });
        kNav.navigate(kRoute.P01);
      })
      .catch((e) => {
        dispatch({
          type: "RIBBON",
          message: new Error(e.message),
        });
      });
    return undefined;
  };

  if (searchParams.get("mode") !== "resetPassword") {
    return (
      <View>
        <Text>Close this window and try again.</Text>
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={[styles.container, rIsMobile ? styles.m5 : styles.m10]}
      style={styles.bgBrandLight}
    >
      <View style={rIsMobile ? styles.wFull : styles.minWContainer}>
        <Text style={[styles.h2, styles.textAlignCenter, styles.mb10]}>Change Password</Text>
        <View style={styles.mb10}>
          <Text style={[styles.h3, styles.mb1]}>New Password</Text>
          <TextInput
            placeholder="New User Password"
            autoCompleteType="password"
            onChangeText={setNewPassword}
            secureTextEntry={true}
            style={styles.inputLogin}
            placeholderTextColor={kColors.gray400}
          />
        </View>
        <View style={styles.mb10}>
          <Text style={[styles.h3, styles.mb1]}>Confirm Password</Text>
          <TextInput
            placeholder="Confirm User Password"
            autoCompleteType="password"
            onChangeText={setConfirmPassword}
            secureTextEntry={true}
            style={styles.inputLogin}
            placeholderTextColor={kColors.gray400}
          />
        </View>
        <PdButton onPress={sendNewPassword} size="lg" isFullWidth={rIsMobile} style={rIsMobile ? styles.mb5 : {}}>
          <PdButtonLabel>Reset Password</PdButtonLabel>
        </PdButton>
      </View>
    </ScrollView>
  );
};

export const P01_04 = React.memo(P01_04_ResetPassword);

const styles = StyleSheet.create({
  bgBrandLight: {
    backgroundColor: kColors.brandLight,
  },
  container: {
    alignItems: "center",
    backgroundColor: kColors.white,
    borderRadius: 4 * kStyleConst.kBorderRadius,
    flexGrow: 1,
    justifyContent: "center",
    padding: 10 * kStyleConst.kSpacing,
  },
  h2: {
    ...kFonts.ralewayLargeLight,
    color: kColors.brand500,
    letterSpacing: 1.1,
    lineHeight: 35,
  },
  h3: {
    ...kFonts.H3,
    color: kColors.darkGray,
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  inputLogin: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    flex: 1,
    height: 36,
    outlineColor: kColors.transparent,
    ...kFonts.poppinsRegularLight,
    letterSpacing: 1,
    lineHeight: 25,
  },
  m10: {
    margin: 10 * kStyleConst.kSpacing,
  },
  m5: {
    margin: 5 * kStyleConst.kSpacing,
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  minWContainer: {
    minWidth: 600,
  },
  textAlignCenter: {
    textAlign: "center",
  },
  wFull: {
    width: "100%",
  },
});
